/**
 * View-only links for a layout: anyone with one can look at the layout in the hall without signing in,
 * and can't change anything. Any lead makes them and turns them off; an admin decides whether they
 * show reciters.
 */

import type { LayoutRow, LinkRow } from '@mawkeb/shared/api';
import { useEffect, useState } from 'react';
import { api, ApiError, viewUrl } from '../api';
import { useSession } from '../state/session';
import { venue } from '../state/store';
import { Dialog } from './Dialog';
import { ago } from './time';

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    venue().toast('Link copied.');
  } catch {
    prompt('Copy this link:', text);
  }
}

export function ShareDialog({ layout, onClose, onChanged }: { layout: LayoutRow; onClose: () => void; onChanged?: (row: LayoutRow) => void }) {
  const admin = useSession((s) => s.me?.role === 'admin');
  const [links, setLinks] = useState<LinkRow[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reciters, setReciters] = useState(layout.showReciters);

  useEffect(() => {
    api
      .links(layout.id)
      .then((r) => setLinks(r.links))
      .catch((e: ApiError) => setProblem(e.status === 'offline' ? 'There’s no connection. Links can be made and turned off once you’re back online.' : e.message));
  }, [layout.id]);

  const make = async () => {
    setBusy(true);
    try {
      const { link } = await api.makeLink(layout.id);
      setLinks((l) => [link, ...(l ?? [])]);
      void copy(viewUrl(link.token));
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const off = async (token: string) => {
    if (!confirm('Turn this link off? Anyone using it will lose access straight away.')) return;
    try {
      await api.linkOff(token);
      setLinks((l) => l?.map((k) => (k.token === token ? { ...k, revokedAt: Date.now() } : k)) ?? null);
    } catch (e) {
      venue().toast((e as Error).message, { tone: 'bad' });
    }
  };

  const toggleReciters = async () => {
    const next = !reciters;
    setReciters(next);
    try {
      const r = await api.changeLayout(layout.id, { showReciters: next });
      onChanged?.(r.layout);
    } catch (e) {
      setReciters(!next);
      venue().toast((e as Error).message, { tone: 'bad' });
    }
  };

  const share = async (url: string) => {
    if ('share' in navigator) {
      try {
        await navigator.share({ title: layout.name, url });
        return;
      } catch {
        /* closed the share sheet */
      }
    } else void copy(url);
  };

  const live = links?.filter((k) => !k.revokedAt) ?? [];
  const old = links?.filter((k) => k.revokedAt) ?? [];

  return (
    <Dialog title={`Share “${layout.name}”`} onClose={onClose} size="wide">
      <p className="lead-text">Anyone with a view-only link can look round this layout in the hall without signing in. They can’t change anything, and a link stops working the moment you turn it off.</p>
      {problem && <p className="form-problem">{problem}</p>}
      <button type="button" className="primary" onClick={() => void make()} disabled={busy || !!problem}>
        {busy ? 'Making a link…' : 'Make a view-only link'}
      </button>
      {live.length > 0 && (
        <ul className="link-list">
          {live.map((k) => (
            <li key={k.token}>
              <input className="link-url" value={viewUrl(k.token)} readOnly onFocus={(e) => e.target.select()} aria-label="View-only link" />
              <div className="link-meta">
                Made by {k.madeBy.name} {ago(k.madeAt)}
                {k.usedAt ? `, last opened ${ago(k.usedAt)}` : ', not opened yet'}
              </div>
              <div className="link-actions">
                <button type="button" className="secondary small" onClick={() => void share(viewUrl(k.token))}>
                  {'share' in navigator ? 'Send' : 'Copy'}
                </button>
                <button type="button" className="secondary small" onClick={() => void copy(viewUrl(k.token))}>
                  Copy
                </button>
                <button type="button" className="secondary small danger-text" onClick={() => void off(k.token)}>
                  Turn off
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {old.length > 0 && <p className="form-note">{old.length === 1 ? 'One link was turned off.' : `${old.length} links were turned off.`}</p>}
      {admin && (
        <label className="row switch-row">
          <span>
            Show reciters to people with a link
            <small>Off unless you turn it on. Only admins see this.</small>
          </span>
          <button type="button" role="switch" className="switch" aria-checked={reciters} onClick={() => void toggleReciters()} aria-label="Show reciters to people with a link" />
        </label>
      )}
    </Dialog>
  );
}
